import { Box, Typography, Button, Menu, MenuItem, ListItemText, TextField } from "@mui/material";
import { colors } from "../../../../../../../theme/muiColors";
import { useMemo, useState, useRef, useEffect } from "react";
import { DateTime } from "luxon";
import { menuPropsModal } from "../../../../../../../utils";
import { BaseModalComponent } from "../../../../../../common/BaseModalComponent";
import { useModals } from "../../../../../../../context/ModalContext";
import { CustomRecurrencyModalContent, RepetitiveExecutionFormState, formatSummary } from "./components/CustomRecurrencyModalContent";
import { EFrequencyType, EWeekdays } from "../../enums/event.enums";
import { IExclusionRule } from "../../interfaces/event.interfaces";
import { getRecurrencyPresets } from "./presets/getRecurrencyPresets";

// Shared subtitle style (kept consistent with CustomRecurrencyModalContent)
const SUBTITLE_SX = { color: "var(--fx-surface-texttertiary, #94A3B8)", fontWeight: 700, fontSize: 13, mb: 1 };

// End date picker used in the main view.
export interface EndDateFieldProps {
  value: string; // "yyyy-LL-dd'T'HH:mm"
  onChange: (raw: string, isoWithOffset: string) => void;
}

export function EndDateField({ value, onChange }: EndDateFieldProps) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={SUBTITLE_SX}>
        End date (optional)
      </Typography>
      <TextField
        type="datetime-local"
        value={value || ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (!raw) return;
          const iso = DateTime.fromISO(raw).setZone("UTC-6").toISO({ includeOffset: true }) as string;
          onChange(raw, iso);
        }}
        size="small"
        fullWidth
        InputProps={{
          sx: {
            background: "var(--fx-surface-sunken, #F8FAFC)",
            borderRadius: "8px",
            "& input": { fontSize: 15, color: "var(--fx-surface-text, #0F172A)", padding: "10px 14px" },
          },
        }}
      />
      <Typography sx={{ fontSize: 11.5, color: "var(--fx-surface-textsecondary, #64748B)", mt: 0.75 }}>
        Leave empty to keep the default end date.
      </Typography>
    </Box>
  );
}

interface Props {
  onChange: (data: any) => void;
  disabledForms?: string[];
  initialData?: any;
  disabled: boolean;
}

export const RepetitiveExecution = ({ onChange, disabledForms, initialData, disabled }: Props) => {
  // Only need closeByKey for closing modal from internal buttons in CustomRecurrencyModalContent
  const { closeByKey } = useModals();
  const customModalTriggerRef = useRef<HTMLButtonElement>(null);

  const [formState, setFormState] = useState<RepetitiveExecutionFormState>(() => {
    if (!initialData) {
      return {
        frequencyType: EFrequencyType.WEEKLY as EFrequencyType | "hourly" | "eachNDays" | "eachNMinutes" | "eachNHours",
        intervalValue: "01",
        each: 1,
        at: "09:30",
        weekdays: [] as EWeekdays[],
        monthDays: [] as string[],
        yearDates: [] as string[],
        endDate: DateTime.now().plus({ months: 2 }).setZone("UTC-6").toISO({ includeOffset: true }),
        exclusions: [] as IExclusionRule[],
        endAt: undefined,
      };
    }

    const freq = initialData.frequency;
    const intervals = freq?.interval || [];

    return {
      frequencyType: freq?.type || initialData.frequencyType || EFrequencyType.WEEKLY,
      intervalValue: "01",
      each: freq?.each || initialData.each || 1,
      at: freq?.at || initialData.at || "09:30",
      endAt: freq?.endAt || initialData.endAt || "10:30",
      weekdays: freq?.type === "weekly" ? intervals.filter((i) => i.type === "weekday").map((i) => i.value) : initialData.weekdays || [],
      monthDays: freq?.type === "monthly" ? intervals.filter((i) => i.type === "date").map((i) => i.value) : initialData.monthDays || [],
      yearDates: freq?.type === "yearly" ? intervals.filter((i) => i.type === "date").map((i) => i.value) : initialData.yearDates || [],
      endDate: initialData.endDate || DateTime.now().plus({ months: 2 }).setZone("UTC-6").toISO({ includeOffset: true }),
      exclusions: freq?.exclusionRules || initialData.exclusions || [],
    };
  });

  useEffect(() => {
    if (initialData?.endDate) {
      setFormState((current) => current.endDate === initialData.endDate ? current : { ...current, endDate: initialData.endDate });
    }
  }, [initialData?.endDate]);

  const presets = useMemo(() => getRecurrencyPresets(), []);
  const [repeatPresetId, setRepeatPresetId] = useState<string>("");
  const [customSummary, setCustomSummary] = useState<string | null>(null);
  const [shouldOpenCustomModal, setShouldOpenCustomModal] = useState(false);
  const CUSTOM_RECURRENCY_MODAL_KEY = "EventScheduler-Recurrency-Custom";

  // Ensure MUI menus render above BaseModalComponent (zIndex ~30000+) by using the same high-zIndex container used across the app.
  const menuContainer = useMemo(() => menuPropsModal.container(), []);
  const menuPaperBaseStyle = (menuPropsModal as any)?.PaperProps?.style;


  // End date input bound to formState.endDate so the user can edit it directly,
  // even without opening the Custom modal.
  const endDateInput = useMemo(
    () => (formState.endDate ? DateTime.fromISO(formState.endDate).toFormat("yyyy-LL-dd'T'HH:mm") : ""),
    [formState.endDate]
  );

  const handleEndDateChange = (raw: string, iso: string) => {
    setFormState((prev) => {
      const next = { ...prev, endDate: iso };
      onChange?.(next);
      return next;
    });
  };

  const [repeatMenuAnchorEl, setRepeatMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [weeklyOnAnchorEl, setWeeklyOnAnchorEl] = useState<null | HTMLElement>(null);
  const repeatMenuOpen = Boolean(repeatMenuAnchorEl);
  const weeklyOnMenuOpen = Boolean(weeklyOnAnchorEl);

  const applyPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset?.apply) return;

    const next = preset.apply(formState);
    setFormState(next);
    onChange?.(next);
  };

  const handleRepeatSelect = (presetId: string) => {
    setRepeatPresetId(presetId);

    if (presetId === "custom") {
      setRepeatMenuAnchorEl(null);
      setWeeklyOnAnchorEl(null);
      // Trigger the modal to open by clicking the hidden trigger
      setShouldOpenCustomModal(true);
      return;
    }

    // Clear custom summary when selecting a preset
    setCustomSummary(null);
    setRepeatMenuAnchorEl(null);
    setWeeklyOnAnchorEl(null);
    applyPreset(presetId);
  };

  // Open modal when shouldOpenCustomModal is true
  useEffect(() => {
    if (shouldOpenCustomModal && customModalTriggerRef.current) {
      customModalTriggerRef.current.click();
      setShouldOpenCustomModal(false);
    }
  }, [shouldOpenCustomModal]);

  const topLevelPresets = presets.filter((p) => !p.parentId);
  const weeklyOnPresets = presets.filter((p) => p.parentId === "weekly-on");
  const selectedPreset = presets.find((p) => p.id === repeatPresetId);

  const repeatButtonLabel = useMemo(() => {
    // If custom is selected and we have a summary, show it
    if (repeatPresetId === "custom" && customSummary) {
      return customSummary;
    }
    // Otherwise show preset label
    if (selectedPreset?.parentId === "weekly-on") {
      return `Weekly on ${selectedPreset.label}`;
    }
    return selectedPreset?.label || "Choose a repeat pattern";
  }, [repeatPresetId, customSummary, selectedPreset]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "var(--fx-surface-text, #0F172A)",
              mb: 0.5,
              lineHeight: 1.4,
            }}
          >
            Repeat
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: "var(--fx-surface-textsecondary, #64748B)",
              lineHeight: 1.5,
            }}
          >
            Configure the recurrency of the service.
          </Typography>
        </Box>

        <Button
          disabled={disabled}
          onClick={(e) => setRepeatMenuAnchorEl(e.currentTarget)}
          fullWidth
          sx={{
            width: "50%",
            background: "var(--fx-surface-sunken, #F8FAFC)",
            borderRadius: "8px",
            px: 1.5,
            py: 1,
            justifyContent: "space-between",
            textTransform: "none",
            fontSize: 13,
            color: colors.grey[800],
          }}
        >
          {repeatButtonLabel}
          <Box sx={{ opacity: 0.6, ml: "auto" }}>▾</Box>
        </Button>

        <Box sx={{ mt: 2, maxWidth: 360 }}>
          <EndDateField value={endDateInput} onChange={handleEndDateChange} />
        </Box>

        <Menu
          container={menuContainer}
          anchorEl={repeatMenuAnchorEl}
          open={repeatMenuOpen}
          onClose={(_, reason) => {
            // If the nested submenu is open, a click-away should only close the submenu (keep the main menu open).
            if (reason === "backdropClick" && weeklyOnMenuOpen) {
              setWeeklyOnAnchorEl(null);
              return;
            }

            setRepeatMenuAnchorEl(null);
            setWeeklyOnAnchorEl(null);
          }}
          disableAutoFocusItem
          MenuListProps={{
            onClick: () => {
              // Any click inside the main menu but outside the submenu should close the submenu.
              if (weeklyOnMenuOpen) setWeeklyOnAnchorEl(null);
            },
          }}
          PaperProps={{
            style: menuPaperBaseStyle,
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 320,
            },
          }}
        >
          {topLevelPresets
            .filter((p) => p.id !== "custom")
            .map((p) => {
              if (p.id === "weekly-on") {
                return (
                  <MenuItem
                    key={p.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setWeeklyOnAnchorEl((prev) => (prev ? null : (e.currentTarget as HTMLElement)));
                    }}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                  >
                    <ListItemText primary={p.label} />
                    <Box sx={{ opacity: 0.6 }}>›</Box>
                  </MenuItem>
                );
              }

              return (
                <MenuItem key={p.id} onClick={() => handleRepeatSelect(p.id)}>
                  {p.label}
                </MenuItem>
              );
            })}

          <MenuItem onClick={() => handleRepeatSelect("custom")}>
            <ListItemText primary={customSummary || "Custom"} />
          </MenuItem>
        </Menu>

        <Menu
          container={menuContainer}
          anchorEl={weeklyOnAnchorEl}
          open={weeklyOnMenuOpen && repeatMenuOpen}
          onClose={() => setWeeklyOnAnchorEl(null)}
          disableAutoFocusItem
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            style: menuPaperBaseStyle,
            sx: {
              borderRadius: 2,
              minWidth: 220,
              ml: 0.5,
            },
          }}
        >
          {weeklyOnPresets.map((p) => (
            <MenuItem key={p.id} onClick={() => handleRepeatSelect(p.id)}>
              {p.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <BaseModalComponent
        modalKey={CUSTOM_RECURRENCY_MODAL_KEY}
        modalLabel="Custom repeat"
        elementTrigger={
          <button
            ref={customModalTriggerRef}
            style={{ display: "none" }}
            aria-hidden="true"
          />
        }
        defaultSizes={{ width: 980 }}
        defaultFullscreen={false}
      >
        <CustomRecurrencyModalContent
          initialValue={formState}
          disabledForms={disabledForms}
          onApply={(next) => {
            setFormState(next);
            setCustomSummary(formatSummary(next));
            onChange?.(next);
            closeByKey(CUSTOM_RECURRENCY_MODAL_KEY);
          }}
          onClose={() => {
            closeByKey(CUSTOM_RECURRENCY_MODAL_KEY);
          }}
          sx={{
            maxHeight: {
              xs: "450px",
              sm: "550px",
              md: "750px",
              lg: "980px",
              xl: "1200px",
            },
          }}
        />
      </BaseModalComponent>
    </Box>
  );
};
